import { useState } from "react";
import { Button } from "./ui/button";
import { AlertTriangle, Send, Loader2, CheckCircle } from "lucide-react";
import { publishApi } from "../lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const PublishBanner = ({ unpublishedCount, onPublished }) => {
  const [publishing, setPublishing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const response = await publishApi.publish();
      toast.success(response.data.message || "Changes published successfully!");
      onPublished?.();
    } catch (error) {
      console.error("Failed to publish:", error);
      toast.error("Failed to publish changes");
    } finally {
      setPublishing(false);
      setConfirmOpen(false);
    }
  };

  if (unpublishedCount === 0) {
    return null;
  }

  return (
    <>
      <div 
        className="bg-amber-500/15 border border-amber-500/30 rounded-lg px-4 py-3 flex items-center justify-between gap-4"
        data-testid="publish-banner"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              You have {unpublishedCount} unpublished {unpublishedCount === 1 ? 'change' : 'changes'}
            </p>
            <p className="text-xs text-amber-700/80 dark:text-amber-300/80">
              Staff cannot see these changes until you publish them
            </p>
          </div>
        </div>
        <Button 
          onClick={() => setConfirmOpen(true)}
          disabled={publishing}
          className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
          data-testid="publish-btn"
        >
          {publishing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          Publish Changes
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Publish Rota Changes
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                You are about to publish <strong>{unpublishedCount} {unpublishedCount === 1 ? 'change' : 'changes'}</strong> to the rota.
              </p>
              <p>
                Once published, staff members will be able to see these changes in their schedule view.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePublish}
              disabled={publishing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {publishing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Publish Now
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PublishBanner;
